﻿import * as styleTypes from './styleTypes';
import { parseGraphic, parseMark, parseAnchorPoint } from './styleUtils';
import * as defaults from '../defaults';
import { hexToRGB } from './canvas/styles/styleUtils';

export function parseStyleRules(rules, type) {
    let featureList = [];
    let labelList = [];

    let manyRules = false;
    let lastRule = '';
    let isTimeDependant = { value: false};

    if (rules != null && rules.length > 0) {
        rules.forEach(rule => {
            rule.Items.forEach(item => {
                if (lastRule == '') {
                    lastRule = item.$type;
                } else if (lastRule != item.$type) {
                    manyRules = true;
                }
            });
        });

        rules.forEach(rule => {
            parseRule(rule, featureList, labelList, type, manyRules, isTimeDependant);
        });
    }

    return {
        styleType: styleTypes.STYLE_TYPE_WEBGL,
        style: {
            featureStyle: featureList,
            labelStyle: labelList,
            isTimeDependant: isTimeDependant.value
        }
    };
}

function parseRule(rule, featureList, labelList, type, cnt, isTimeDependant) {
    if (rule.Items != null && rule.Items.length > 0) {
        rule.Items.forEach(item => {
            switch (item.$type) {
                case styleTypes.POINT: parsePoint(item, rule, featureList, type, cnt, isTimeDependant);
                    break;
                case styleTypes.LINE: parseLine(item, rule, featureList, labelList, type, cnt);
                    break;
                case styleTypes.POLYGON: parsePolygon(item, rule, featureList, labelList, type, cnt);
                    break;
                case styleTypes.LABEL: parseLabel(item, rule, featureList, labelList, type, cnt);
                    break;
                default: break;
            }
        });
    }
}

function parseLabel(item, rule, featureList, type, cnt, isTimeDependant) {
}

function parsePoint(item, rule, featureList, type, cnt, isTimeDependant) {
    if (item.Graphic != null) {
        if (item.Graphic.Mark != null) {
            parseGraphicMarkPoint(item, rule, featureList, type, cnt, isTimeDependant);
        } else if (item.Graphic.ExternalGraphic != null) {
            parseGraphicExternalGraphicPoint(item, rule, featureList, type, cnt, isTimeDependant);
        }
    }
}

function parseGraphicExternalGraphicPoint(item, rule, featureList, type, cnt) {
    let graphicItem = parseGraphic(item.Graphic);
    let anchorPoint = parseAnchorPoint(item.Graphic);
    let onlineResource = item.Graphic.ExternalGraphic.OnlineResource != null ? item.Graphic.ExternalGraphic.OnlineResource : defaults.DEFAULT_STYLE_ONLINE_RESOURCE;

    let filter = ['all', getTypeFilter(type)];

    let style = {
        'icon-src': onlineResource,
        'icon-width': graphicItem.radius,
        'icon-height': graphicItem.radius,
        'icon-rotation': (Math.PI * (graphicItem.rotation)) / 180.0,
        'icon-displacement': [graphicItem.displacementX, graphicItem.displacementY],
        'icon-anchor': [anchorPoint.anchorPointX, anchorPoint.anchorPointX],
        'icon-opacity': graphicItem.opacity
    };

    if (cnt) {
       filter.push(['==', ['geometry-type'], 'Point']);
    }

    handleFilter(rule.Item, filter);

    if (filter.length == 2) {
        filter = filter[1];
    }

    handleAnimation(item.Graphic, rule, featureList, type, cnt, item);

    featureList.push({ style: style, filter: filter });
}

function parseGraphicMarkPoint(item, rule, featureList, type, cnt, isTimeDependant) {
    let mark = item.Graphic.Mark;

    if (mark.WellKnownName != null) {
        switch (mark.WellKnownName) {
            case styleTypes.WKN_CIRCLE: parseGraphicMarkCirclePoint(item.Graphic, rule, featureList, type, cnt, item, isTimeDependant);
                break;
            case styleTypes.WKN_SQUARE: parseGraphicMarkSquarePoint(item.Graphic, rule, featureList, type, cnt, item, isTimeDependant);
                break;
            case styleTypes.WKN_TRIANGLE: parseGraphicMarkTrianglePoint(item.Graphic, rule, featureList, type, cnt, item, isTimeDependant);
                break;
            case styleTypes.WKN_STAR: parseGraphicMarkStarPoint(item.Graphic, rule, featureList, type, cnt, item, isTimeDependant);
                break;
            case styleTypes.WKN_CROSS: parseGraphicMarkCrossPoint(item.Graphic, rule, featureList, type, cnt, item, isTimeDependant);
                break;
            case styleTypes.WKN_X: parseGraphicMarkXPoint(item.Graphic, rule, featureList, type, cnt, item, isTimeDependant);
                break;
            default: break;
        }
    }
}

function parseGraphicMarkCirclePoint(graphic, rule, featureList, type, cnt, item, isTimeDependant) {
    let graphicItem = parseGraphic(graphic);
    let mark = parseMark(graphic.Mark);

    let filter = ['all', getTypeFilter(type)];

    let style = {
        'circle-radius': graphicItem.radius,
        'circle-rotation': (Math.PI * (graphicItem.rotation + 45)) / 180.0,
        'circle-fill-color': mark.fillColor,
        'circle-opacity': mark.fillOpacity,
        'circle-stroke-color': mark.strokeColor,
        'circle-stroke-opacity': mark.strokeOpacity,
        'circle-stroke-width': mark.strokeWidth,
        'circle-displacement': [graphicItem.displacementX, graphicItem.displacementY]
    };

    if (cnt) {
        filter.push(['==', ['geometry-type'], 'Point']);
    }

    handleFilter(rule.Item, filter);

    if (filter.length == 2) {
        filter = filter[1];
    }

    handleAnimation(graphic, rule, featureList, type, cnt, item, isTimeDependant);

    featureList.push({ style: style, filter: filter });
}

function handleAnimation(graphic, rule, featureList, type, cnt, item, isTimeDependant) {
    if (item.Extra != null && item.Extra.Animation != null) {
        isTimeDependant.value = true;
        let radius = item.Extra.Animation.Radius != null ? item.Extra.Animation.Radius : 25
        let duration = item.Extra.Animation.Duration != null ? item.Extra.Animation.Duration : 4;
        let opacity = item.Extra.Animation.Opacity != null ? item.Extra.Animation.Opacity : 0;
        let color = item.Extra.Animation.Color != null ? item.Extra.Animation.Color : '#ff0000';
        let width = item.Extra.Animation.Width != null ? item.Extra.Animation.Width : 2;

        let filter = ['all', getTypeFilter(type)];

        let graphicItem = parseGraphic(graphic);
        let animation = {
            'shape-radius': [
                'interpolate',
                ['linear'],
                ['sin', ['*', ['/', ['time'], duration], Math.PI * 2]],
                -1, 1,
                1, graphicItem.radius + radius
            ],
            'shape-stroke-color': hexToRgbArray(color),
            'shape-stroke-width': width,
            'shape-fill-color': hexToRgbArray(color, opacity),
            'shape-points': 4,
        };

        if (cnt) {
            filter.push(['==', ['geometry-type'], 'Point']);
        }

        if (item.Extra.Animation.Condition != null) {
            filter.push(eval(item.Extra.Animation.Condition));
        }

        handleFilter(rule.Item, filter);

        if (filter.length == 2) {
            filter = filter[1];
        }

        featureList.push({ style: animation, filter: filter });
    }
}

function hexToRgbArray(hex, alpha) {
    // Ensure hex starts with '#' and is 7 characters long
    if (hex.startsWith('#')) {
        hex = hex.slice(1); // Remove the '#' symbol
    }

    // If the hex color is 3 characters long, expand it to 6 characters
    if (hex.length === 3) {
        hex = hex.split('').map(function (char) {
            return char + char; // Repeat each character (e.g., 'f' -> 'ff')
        }).join('');
    }

    if (alpha == null) {
        alpha = 1;
    }

    // Parse the R, G, B components from the hex string
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    // Return the color array in the required format
    return ['color', r, g, b, alpha];
}

function parseGraphicMarkSquarePoint(graphic, rule, featureList, type, cnt, item, isTimeDependant) {
    let graphicItem = parseGraphic(graphic);
    let mark = parseMark(graphic.Mark);

    let filter = ['all', getTypeFilter(type)];

    let style = {
        'shape-radius': graphicItem.radius,
        'shape-rotation': (Math.PI * (graphicItem.rotation + 45)) / 180.0,
        'shape-fill-color': mark.fillColor,
        'shape-opacity': mark.fillOpacity,
        'shape-stroke-color': mark.strokeColor,
        'shape-stroke-opacity': mark.strokeOpacity,
        'shape-stroke-width': mark.strokeWidth,
        'shape-points': 4,
        'shape-displacement': [graphicItem.displacementX, graphicItem.displacementY]
    };

    if (cnt) {
        filter.push(['==', ['geometry-type'], 'Point']);
    }

    handleFilter(rule.Item, filter);

    if (filter.length == 2) {
        filter = filter[1];
    }

    handleAnimation(graphic, rule, featureList, type, cnt, item, isTimeDependant);

    featureList.push({ style: style, filter: filter });
}

function parseGraphicMarkTrianglePoint(graphic, rule, featureList, type, cnt, item, isTimeDependant) {
    let graphicItem = parseGraphic(graphic);
    let mark = parseMark(graphic.Mark);

    let filter = ['all', getTypeFilter(type)];

    let style = {
        'shape-radius': graphicItem.radius,
        'shape-rotation': (Math.PI * graphicItem.rotation) / 180.0,
        'shape-fill-color': mark.fillColor,
        'shape-opacity': mark.fillOpacity,
        'shape-stroke-color': mark.strokeColor,
        'shape-stroke-opacity': mark.strokeOpacity,
        'shape-stroke-width': mark.strokeWidth,
        'shape-points': 3,
        'shape-displacement': [graphicItem.displacementX, graphicItem.displacementY]
    };

    if (cnt) {
        filter.push(['==', ['geometry-type'], 'Point']);
    }

    handleFilter(rule.Item, filter);

    if (filter.length == 2) {
        filter = filter[1];
    }

    handleAnimation(graphic, rule, featureList, type, cnt, item, isTimeDependant);

    featureList.push({ style: style, filter: filter });
}

function parseGraphicMarkStarPoint(graphic, rule, featureList, type, cnt, item, isTimeDependant) {
    let graphicItem = parseGraphic(graphic);
    let mark = parseMark(graphic.Mark);

    let filter = ['all', getTypeFilter(type)];

    let style = {
        'shape-radius': graphicItem.radius,
        'shape-radius2': graphicItem.radius / 2.5,
        'shape-rotation': (Math.PI * graphicItem.rotation) / 180.0,
        'shape-fill-color': mark.fillColor,
        'shape-opacity': mark.fillOpacity,
        'shape-stroke-color': mark.strokeColor,
        'shape-stroke-opacity': mark.strokeOpacity,
        'shape-stroke-width': mark.strokeWidth,
        'shape-points': 5,
        'shape-displacement': [graphicItem.displacementX, graphicItem.displacementY]
    };

    if (cnt) {
        filter.push(['==', ['geometry-type'], 'Point']);
    }

    handleFilter(rule.Item, filter);

    if (filter.length == 2) {
        filter = filter[1];
    }

    handleAnimation(graphic, rule, featureList, type, cnt, item, isTimeDependant);

    featureList.push({ style: style, filter: filter });
}

function parseGraphicMarkCrossPoint(graphic, rule, featureList, type, cnt, item, isTimeDependant) {
    let graphicItem = parseGraphic(graphic);
    let mark = parseMark(graphic.Mark);

    let filter = ['all', getTypeFilter(type)];

    let style = {
        'shape-angle': 0,
        'shape-radius': graphicItem.radius,
        'shape-radius2': 0,
        'shape-rotation': (Math.PI * graphicItem.rotation) / 180.0,
        'shape-fill-color': mark.fillColor,
        'shape-opacity': mark.fillOpacity,
        'shape-stroke-color': mark.strokeColor,
        'shape-stroke-opacity': mark.strokeOpacity,
        'shape-stroke-width': mark.strokeWidth,
        'shape-points': 4,
        'shape-displacement': [graphicItem.displacementX, graphicItem.displacementY]
    };

    if (cnt) {
        filter.push(['==', ['geometry-type'], 'Point']);
    }

    handleFilter(rule.Item, filter);

    if (filter.length == 2) {
        filter = filter[1];
    }

    handleAnimation(graphic, rule, featureList, type, cnt, item, isTimeDependant);

    featureList.push({ style: style, filter: filter });
}

function parseGraphicMarkXPoint(graphic, rule, featureList, type, cnt, item) {
    let graphicItem = parseGraphic(graphic);
    let mark = parseMark(graphic.Mark);

    let filter = ['all', getTypeFilter(type)];

    let style = {
        'shape-angle': Math.PI / 4,
        'shape-radius': graphicItem.radius,
        'shape-radius2': 0,
        'shape-rotation': (Math.PI * graphicItem.rotation) / 180.0,
        'shape-fill-color': mark.fillColor,
        'shape-opacity': mark.fillOpacity,
        'shape-stroke-color': mark.strokeColor,
        'shape-stroke-opacity': mark.strokeOpacity,
        'shape-stroke-width': mark.strokeWidth,
        'shape-points': 4,
        'shape-displacement': [graphicItem.displacementX, graphicItem.displacementY]
    };

    if (cnt) {
        filter.push(['==', ['geometry-type'], 'Point']);
    }

    handleFilter(rule.Item, filter);

    if (filter.length == 2) {
        filter = filter[1];
    }

    handleAnimation(graphic, rule, featureList, type, cnt, item, isTimeDependant);

    featureList.push({ style: style, filter: filter });
}

function parseLine(item, rule, featureList, labelList, type, cnt) {
    if (item.Stroke != null && item.Stroke.styling != null) {
        let stroke = item.Stroke.styling;

        let strokeColor = null;

        if (stroke.stroke.prop != null) {
            strokeColor = ['get', stroke.stroke.prop, '#000000'];
        } else {
            strokeColor = hexToRGB(stroke.stroke, stroke.strokeOpacity);
        }

        let filter = ['all', getTypeFilter(type)];

        let style = {
            'stroke-color': strokeColor,
            'stroke-width': stroke.strokeWidth,
            'stroke-line-cap': stroke.strokeLinecap,
            'stroke-line-join': stroke.strokeLinejoin
        };

        if (cnt) {
            filter.push(['==', ['geometry-type'], 'LineString']);
        }

        if (stroke.strokeDasharray != null && stroke.strokeDasharray.trim().length > 0) {
            let dashArray = stroke.strokeDasharray.split(' ');

            style['stroke-line-dash'] = [];

            dashArray.forEach(dash => {
                style['stroke-line-dash'].push(dash * 1);
            });

            if (stroke.strokeDashoffset != null) {
                style['stroke-line-dash-offset'] = stroke.strokeDashoffset;
            } else {
                style['stroke-line-dash-offset'] = 0;
            }
        }

        handleFilter(rule.Item, filter);

        if (filter.length == 2) {
            filter = filter[1];
        }

        featureList.push({ style: style, filter: filter });
    }
    else if (item.Stroke != null && item.Stroke.GraphicStroke != null && item.Stroke.GraphicStroke.Graphic != null && item.Stroke.GraphicStroke.Graphic.ExternalGraphic != null) {
        let externalGraphic = item.Stroke.GraphicStroke.Graphic.ExternalGraphic;
        let graphicItem = parseGraphic(item.Stroke.GraphicStroke.Graphic);

        let filter = ['all', getTypeFilter(type)];

        let style = {
            'stroke-opacity': 0.1,
            'stroke-width': graphicItem.radius * 2,
            'stroke-pattern-src': externalGraphic.OnlineResource != null ? externalGraphic.OnlineResource : defaults.DEFAULT_STYLE_ONLINE_RESOURCE
        };

        if (cnt) {
            filter.push(['==', ['geometry-type'], 'LineString']);
        }

        handleFilter(rule.Item, filter);

        if (filter.length == 2) {
            filter = filter[1];
        }

        featureList.push({ style: style, filter: filter });
    }
}

function parsePolygon(item, rule, featureList, labelList, type, cnt) {
    let filter = ['all', getTypeFilter(type)];

    let style = {};

    if (cnt) {
        filter.push(['==', ['geometry-type'], 'Polygon']);
    }

    handleFilter(rule.Item, filter);

    if (filter.length == 2) {
        filter = filter[1];
    }

    if (item.Stroke != null && item.Stroke.styling != null) {
        let stroke = item.Stroke.styling;

        let strokeColor = null;

        if (stroke.stroke.prop != null) {
            strokeColor = ['get', stroke.stroke.prop, '#000000'];
        } else {
            strokeColor = hexToRGB(stroke.stroke, stroke.strokeOpacity);
        }

        style['stroke-color'] = strokeColor;
        style['stroke-width'] = stroke.strokeWidth;
        style['stroke-line-cap'] = stroke.strokeLinecap;
        style['stroke-line-join'] = stroke.strokeLinejoin;

        if (stroke.strokeDasharray != null && stroke.strokeDasharray.trim().length > 0) {
            let dashArray = stroke.strokeDasharray.split(' ');

            style['stroke-line-dash'] = [];

            dashArray.forEach(dash => {
                style['stroke-line-dash'].push(dash * 1);
            });

            if (stroke.strokeDashoffset != null) {
                style['stroke-line-dash-offset'] = stroke.strokeDashoffset;
            } else {
                style['stroke-line-dash-offset'] = 0;
            }
        }
    }
    else if (item.Stroke != null && item.Stroke.GraphicStroke != null && item.Stroke.GraphicStroke.Graphic != null && item.Stroke.GraphicStroke.Graphic.ExternalGraphic != null) {
        let externalGraphic = item.Stroke.GraphicStroke.Graphic.ExternalGraphic;
        let graphicItem = parseGraphic(item.Stroke.GraphicStroke.Graphic);

        style['stroke-width'] = graphicItem.radius * 2;
        style['stroke-pattern-src'] = externalGraphic.OnlineResource != null ? externalGraphic.OnlineResource : defaults.DEFAULT_STYLE_ONLINE_RESOURCE;
    }

    if (item.Fill != null && item.Fill.styling != null && item.Fill.styling.fillOpacity != null && item.Fill.styling.fillOpacity != 0) {
        let fill = item.Fill.styling;

        let fillColor = null;

        if (fill.fill.prop != null) {
            fillColor = ['get', fill.fill.prop, '#000000'];
        } else {
            fillColor = hexToRGB(fill.fill, fill.fillOpacity);
        }

        style['fill-color'] = fillColor;
    }

    featureList.push({ style: style, filter: filter });
}

function getTypeFilter(type) {
    return ['==', ['get', 'xstate'], type];
}

function handleFilter(rule, filter) {
    if (rule != null) {
        if (rule.AND != null) {
            let current = [];
            current.push('all');

            handleFilter(handleFilter(rule.AND[0], current));
            handleFilter(handleFilter(rule.AND[1], current));

            filter.push(current);
        }
        else if (rule.OR != null) {
            let current = [];
            current.push('any');

            handleFilter(handleFilter(rule.OR[0], current));
            handleFilter(handleFilter(rule.OR[1], current));

            filter.push(current);
        } else {
            let current = [];
            convertFilterComparison(rule, current);
            filter.push(current);
        }
    }
}

function convertFilterComparison(filter, item) {
    if (filter[styleTypes.FILTER_COMPARISON_GREATER_THAN]) {
        let tuples = filter[styleTypes.FILTER_COMPARISON_GREATER_THAN]
        item.push('>');
        item.push(['get', tuples[0]]);
        item.push(tuples[1]);
    }

    if (filter[styleTypes.FILTER_COMPARISON_GREATER_THAN_OR_EQUAL]) {
        let tuples = filter[styleTypes.FILTER_COMPARISON_GREATER_THAN_OR_EQUAL]
        item.push('>=');
        item.push(['get', tuples[0]]);
        item.push(tuples[1]);
    }

    if (filter[styleTypes.FILTER_COMPARISON_LOWER_THAN]) {
        let tuples = filter[styleTypes.FILTER_COMPARISON_LOWER_THAN]
        item.push('<');
        item.push(['get', tuples[0]]);
        item.push(tuples[1]);
    }

    if (filter[styleTypes.FILTER_COMPARISON_LOWER_THAN_OR_EQUAL]) {
        let tuples = filter[styleTypes.FILTER_COMPARISON_LOWER_THAN_OR_EQUAL]
        item.push('<=');
        item.push(['get', tuples[0]]);
        item.push(tuples[1]);
    }

    if (filter[styleTypes.FILTER_COMPARISON_EQUAL]) {
        let tuples = filter[styleTypes.FILTER_COMPARISON_EQUAL]
        item.push('==');
        item.push(['get', tuples[0]]);
        item.push(tuples[1].replaceAll("'", "").replaceAll('"', ''));
    }

    if (filter[styleTypes.FILTER_COMPARISON_NOT_EQUAL]) {
        let tuples = filter[styleTypes.FILTER_COMPARISON_NOT_EQUAL]
        item.push('!=');
        item.push(['get', tuples[0]]);
        item.push(tuples[1]);
    }

    //if (filter[styleTypes.FILTER_COMPARISON_BETWEEN]) {
    //    operator = styleTypes.FILTER_COMPARISON_BETWEEN;
    //    result.operator = styleTypes.FILTER_COMPARISON_BETWEEN_OPERATOR;
    //}

    //if (filter[styleTypes.FILTER_COMPARISON_LIKE]) {
    //    operator = styleTypes.FILTER_COMPARISON_LIKE;
    //    result.operator = styleTypes.FILTER_COMPARISON_LIKE_OPERATOR;
    //}

    //if (filter[styleTypes.FILTER_COMPARISON_IS_NULL]) {
    //    operator = styleTypes.FILTER_COMPARISON_IS_NULL;
    //    result.operator = styleTypes.FILTER_COMPARISON_IS_NULL_OPERATOR;
    //}

    //let tuples = filter[operator];

    //result.propertyname = tuples[0];

    //if (operator == styleTypes.FILTER_COMPARISON_LIKE) {
    //    result.wildcard = '%';
    //    result.singlechar = '';
    //    result.escapechar = null;
    //}

    //if (operator == styleTypes.FILTER_COMPARISON_BETWEEN) {
    //    result.lowerboundary = tuples[1];
    //    result.upperboundary = tuples[2];
    //} else if (operator != styleTypes.FILTER_COMPARISON_IS_NULL) {
    //    result.literal = tuples[1];
    //}
}